import { PageContentTypes } from '@fm-nx/constants';
import { GetUrlByPageTypeProps, LinkProps } from './types';
import { TypeCategorySkeleton, TypePageSkeleton, TypePageWithoutUnresolvedLink } from '@fm-nx/contentful-types';

export const getUrlByPageType = (props: GetUrlByPageTypeProps): LinkProps => {
    const { pageType, slug } = props;

    // graphQL __typename is capitalized
    const pageTypeNoramlized = String(pageType).charAt(0).toLowerCase() + String(pageType).slice(1);

    if (slug === 'home') {
        return {
            href: '/',
        };
    }
    switch (pageTypeNoramlized) {
        case PageContentTypes.Landing: {
            if (slug === 'journal') {
                return {
                    href: '/about/journal',
                };
            }
            return {
                href: `/${slug}`,
            };
        }
        case PageContentTypes.Company: {
            return {
                href: `/about/${slug}`,
            };
        }
        case PageContentTypes.Journal: {
            return {
                href: `/about/journal/post/${slug}`,
            };
        }
        case PageContentTypes.Person: {
            return {
                href: `/about/team/${slug}`,
            };
        }
        case PageContentTypes.Service:
        case 'service': {
            return {
                href: `/services/service/${slug}`,
            };
        }
        case PageContentTypes.Expertise:
        case 'expertise': {
            return {
                href: `/expertise/${slug}`,
            };
        }
        case PageContentTypes.Industry: {
            return {
                href: `/industries/${slug}`,
            };
        }
        case PageContentTypes.Technology:
        case 'technology': {
            return {
                href: `/partners-and-platforms/${slug}`,
            };
        }
        case PageContentTypes.CaseStudy: {
            return {
                href: `/case-studies/study/${slug}`,
            };
        }

        default: {
            throw new Error(`Page type is not supported yet: ${pageType}`);
        }
    }
};

interface GetLinkToParentProps {
    pathname: string;
}

export const getLinkToParent = ({ pathname }: GetLinkToParentProps): { href: string; label: string } | undefined => {
    const trail = getBreadcrumbTrail({ pathname });
    return trail?.[trail.length - 1];
};

// Returns the full chain of ancestor links (excluding home, which the breadcrumb
// component always renders, and excluding the current page itself).
export const getBreadcrumbTrail = ({
    pathname,
}: GetLinkToParentProps): { href: string; label: string }[] | undefined => {
    if (!pathname) {
        throw new Error('Pathname is required to get breadcrumb trail');
    }

    if (pathname.startsWith('/about/')) {
        if (pathname.includes('apply')) {
            return [{ href: '/about/careers', label: 'Work at Foster Made' }];
        }
        if (pathname.startsWith('/about/team/')) {
            return [{ href: '/about/team', label: 'Our Team' }];
        }
        if (pathname.startsWith('/about/journal/post/')) {
            return [{ href: '/about/journal', label: 'The Journal' }];
        }
        return [{ href: '/about', label: 'About' }];
    }

    if (pathname.startsWith('/services/')) {
        return [{ href: '/services', label: 'Services' }];
    }

    if (pathname.startsWith('/case-studies/study/')) {
        return [{ href: '/case-studies', label: 'Case Studies' }];
    }

    if (pathname.startsWith('/expertise/')) {
        return [
            { href: '/expertise', label: 'Expertise' },
            { href: '/expertise#what-we-do', label: 'What We Do' },
        ];
    }

    if (pathname.startsWith('/partners-and-platforms/')) {
        return [{ href: '/partners-and-platforms', label: 'Platforms & Partnerships' }];
    }

    if (pathname.startsWith('/industries/')) {
        return [
            { href: '/expertise', label: 'Expertise' },
            { href: '/expertise#industries', label: 'Who We Serve' },
        ];
    }

    return undefined;
};

// deprecated, use getLinkToParent instead - remove after convert to graphql
export const getLinkToParentByPage = ({ page }: GetLinkToForPageProps): { href: string; label: string } => {
    const pageType = page?.sys.contentType.sys.id;

    switch (pageType) {
        case PageContentTypes.Company: {
            if (page?.fields.slug?.toString() === 'apply') {
                return { href: '/about/careers', label: 'Work at Foster Made' };
            }
            return { href: '/about', label: 'About' };
        }
        case PageContentTypes.Person: {
            return { href: '/about/team', label: 'Our Team' };
        }
        case PageContentTypes.Journal: {
            return { href: '/about/journal', label: 'The Journal' };
        }
        case PageContentTypes.Industry: {
            return { href: '/expertise', label: 'Expertise' };
        }
        case PageContentTypes.Service: {
            return { href: '/services', label: 'Services' };
        }
        case PageContentTypes.Expertise: {
            return { href: '/expertise', label: 'Expertise' };
        }
        case PageContentTypes.Technology: {
            return { href: '/expertise', label: 'Expertise' };
        }
        case PageContentTypes.CaseStudy: {
            return { href: '/case-studies', label: 'Case Studies' };
        }

        default: {
            // console.log(`Parent not set for page type: ${pageType}`);
            return { href: '#', label: '' };
        }
    }
};

interface GetLinkToForPageProps {
    page?: TypePageWithoutUnresolvedLink | TypePageSkeleton | TypeCategorySkeleton | undefined;
}

export const getLinkToForPage = (props: GetLinkToForPageProps): LinkProps => {
    const { page } = props;
    const slug = page?.fields.slug?.toString();
    const pageType = page?.sys.contentType.sys.id;
    if (!pageType || !slug) {
        throw new Error(`Page type or slug is missing: ${JSON.stringify({ pageType, slug })}`);
    }
    return getUrlByPageType({ pageType, slug });
};
